import React, { useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import useLanguage from '../../customHooks/useLanguage';
import {
    getCalendarLocaleData,
    getMonthsWithEvents,
    getWeeksWithEvents,
} from '../../utils/calendar/calendarUtils';
import {
    CalendarEvent,
    CalendarView,
    CalendarViewType,
} from '../../types/calendar/calendarTypes';
import '../../stylesheets/stylesComponents/calendar.scss';
import LoadingPlaceholder from '../../muiComponents/LoadingPlaceholder';
import Card from '../../muiComponents/Card';
import {
    dateInTenYears,
    dateTenYearsAgo,
    daysBetween,
    isDateless,
} from '../../utils/formatDates';
import HeaderCalendar from './HeaderCalendar';
import useTranslate from '../../customHooks/useTranslate';
import { Alert } from '@mui/material';

interface CalendarProps<T extends object = object> {
    activeView?: CalendarViewType;
    calendarRef: React.RefObject<FullCalendar>;
    viewButtons?: ViewButton[];
    events?: CalendarEvent<T>[];
    renderEvent: (event: CalendarEvent<T>) => React.ReactNode;
    eventColor: string;
    isLoading?: boolean;
    onViewChange: (view: CalendarViewType) => void;
    onPrev: () => void;
    onNext: () => void;
    onToday: () => void;
    onGoToDate: (date: string | Date) => void;
    headerProps?: {
        titleButtonbarEnd?: React.ReactNode;
    };
}

interface EventsWithFakeDatesWhenDateless<T extends object = object>
    extends CalendarEvent<T> {
    start: string | Date;
    end: string | Date;
}

interface ViewButton {
    view: CalendarViewType;
    label: string;
}

const DEFAULT_VIEW_BUTTONS: ViewButton[] = [
    { view: CalendarView.WEEK, label: 'week' },
    { view: CalendarView.MONTH, label: 'month' },
];

const Calendar = <T extends object = object>(props: CalendarProps<T>) => {
    const {
        calendarRef,
        viewButtons = DEFAULT_VIEW_BUTTONS,
        activeView = CalendarView.MONTH,
        events = [],
        renderEvent,
        eventColor,
        isLoading = false,
        onViewChange,
        onPrev,
        onNext,
        onToday,
        onGoToDate,
        headerProps = {},
    } = props;
    const translate = useTranslate();
    const language = useLanguage();
    const [currentPeriodTitle, setCurrentPeriodTitle] = useState('');

    const calendarSettings = useMemo(() => {
        const { locale, firstDay } = getCalendarLocaleData(language);
        return {
            locale,
            firstDay,
        };
    }, [language]);

    const eventsWithFakeDatesWhenDateless: EventsWithFakeDatesWhenDateless<T>[] =
        useMemo(() => {
            // For events without start/end dates, we set a fake date in order to show them in the calendar and then filter them out if needed
            return events.map(item => {
                const startDateless = isDateless(item.start);

                const endDateless = isDateless(item.end);
                return {
                    ...item,
                    start: startDateless
                        ? dateTenYearsAgo
                        : (item.start as string | Date),
                    end: endDateless
                        ? dateInTenYears
                        : (item.end as string | Date),
                };
            });
        }, [events]);

    const periodOptions = useMemo(() => {
        const filteredEvents = events.filter(event => event.start && event.end);

        return activeView === CalendarView.WEEK
            ? getWeeksWithEvents(
                  filteredEvents,
                  language,
                  calendarSettings.firstDay
              )
            : getMonthsWithEvents(filteredEvents, language);
    }, [eventsWithFakeDatesWhenDateless, activeView, language]);

    useEffect(() => {
        if (!eventsWithFakeDatesWhenDateless.length) return;
        const checkIns = eventsWithFakeDatesWhenDateless
            .filter(event => event.start && event.start !== dateTenYearsAgo)
            .map(event => new Date(event.start).getTime());
        const checkOuts = eventsWithFakeDatesWhenDateless
            .filter(event => event.end && event.end !== dateInTenYears)
            .map(event => new Date(event.end).getTime());
        if (!checkIns.length || !checkOuts.length) return;
        const from = new Date(Math.min(...checkIns));
        const to = new Date(Math.max(...checkOuts));
        const days = daysBetween(from, to);

        if (days <= 7) onViewChange(CalendarView.WEEK);
        else onViewChange(CalendarView.MONTH);

        onGoToDate(from);
    }, [eventsWithFakeDatesWhenDateless]);

    return (
        <div
            className='flex-grow height100perc flex-column gap-5'
            style={{ inset: 0, height: '100%' }}
        >
            {isLoading ? (
                <Card className='height100perc'>
                    <LoadingPlaceholder variant='rectangular' height={'100%'} />
                </Card>
            ) : (
                <>
                    {events.length === 0 && (
                        <Card>
                            <Alert severity='info'>
                                {events.length === 0 &&
                                    !isLoading &&
                                    translate('no_results')}
                            </Alert>
                        </Card>
                    )}
                    <HeaderCalendar
                        currentPeriodTitle={currentPeriodTitle}
                        onViewChange={onViewChange}
                        onPrev={onPrev}
                        onNext={onNext}
                        onToday={onToday}
                        onGoToDate={onGoToDate}
                        activeView={activeView}
                        periodOptions={periodOptions}
                        viewButtons={viewButtons}
                        {...headerProps}
                    />

                    <FullCalendar
                        ref={calendarRef}
                        plugins={[
                            dayGridPlugin,
                            interactionPlugin,
                            multiMonthPlugin,
                        ]}
                        initialView={activeView}
                        events={eventsWithFakeDatesWhenDateless}
                        multiMonthMaxColumns={1}
                        dayMinWidth={0}
                        contentHeight='auto'
                        headerToolbar={false}
                        datesSet={dateInfo =>
                            setCurrentPeriodTitle(dateInfo.view.title)
                        }
                        eventContent={eventInfo => {
                            const event: CalendarEvent<T> = {
                                id: eventInfo.event.id,
                                title: eventInfo.event.title,
                                start: eventInfo.event.startStr,
                                end: eventInfo.event.endStr,
                                allDay: eventInfo.event.allDay,
                                extendedProps: eventInfo.event
                                    .extendedProps as T,
                            };
                            return renderEvent(event);
                        }}
                        eventColor={eventColor}
                        {...calendarSettings}
                    />
                </>
            )}
        </div>
    );
};

export default Calendar;
